import React, { memo, useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { buildHillPath, hillY, layoutDots, zoneColor } from './hillGeometry';

// Where the ghost dot sits on an empty hill: early on the uphill, which is
// where real work starts.
const PLACEHOLDER_FRACTION = 0.12;

const SVG_W = 640;
const HILL_H = 200;
const AXIS_H = 28;
const BASELINE = HILL_H - 16;
const AMPLITUDE = 148;
const RADIUS = 10;
// How far a pointer must travel before we treat the gesture as a drag
// rather than a click that selects the scope.
const DRAG_THRESHOLD_PX = 3;


/**
 * Renders scopes as dots on a Shape Up hill. Positions come from the server
 * already resolved; dragging a dot reports a new position to the parent.
 *
 * Past positions are drawn as breadcrumbs rather than a connected line: a dot
 * that jumped from one side of the hill to the other never travelled the straight
 * path between them, and drawing that line would invent history.
 */
const HillChart = ({
  scopes = [],
  selectedScopeId = null,
  onSelectScope,
  onMoveScope,
  onPlaceholderClick,
  readOnly = false,
  trails = {},
  showTrails = false,
  maxWidth,
}) => {
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [hoveredScope, setHoveredScope] = useState(null);
  const movedRef = useRef(false);
  const startXRef = useRef(0);

  const positionFromEvent = useCallback((event) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    if (!rect.width) return 0;
    const fraction = (event.clientX - rect.left) / rect.width;
    return Math.min(1, Math.max(0, fraction));
  }, []);

  const handlePointerDown = (event, scope) => {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    movedRef.current = false;
    startXRef.current = event.clientX;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ scopeId: scope.id, position: scope.position ?? 0 });
  };

  const handlePointerMove = (event) => {
    if (!drag) return;
    if (Math.abs(event.clientX - startXRef.current) > DRAG_THRESHOLD_PX) {
      movedRef.current = true;
    }
    setDrag(current => (current ? { ...current, position: positionFromEvent(event) } : current));
  };

  // Without this a cancelled gesture would leave the dot stuck to the cursor.
  const handlePointerCancel = () => {
    setDrag(null);
    movedRef.current = false;
  };

  const handlePointerUp = (event, scope) => {
    if (!drag) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const didMove = movedRef.current;
    const position = didMove ? positionFromEvent(event) : null;
    setDrag(null);
    movedRef.current = false;

    if (didMove && position !== null && onMoveScope) {
      onMoveScope(scope, Math.round(position * 1000) / 1000);
    } else if (!didMove && onSelectScope) {
      onSelectScope(scope);
    }
  };

  // While dragging, show the dot under the cursor rather than waiting for the
  // server round trip.
  const displayScopes = scopes.map(scope => (
    drag && drag.scopeId === scope.id
      ? { ...scope, position: drag.position, positionSource: 'override' }
      : scope
  ));

  const dots = layoutDots(displayScopes, {
    width: SVG_W,
    baseline: BASELINE,
    amplitude: AMPLITUDE,
    radius: RADIUS,
  });

  const peakX = SVG_W / 2;
  const peakY = BASELINE - AMPLITUDE;

  return (
    <Box sx={{ width: '100%', maxWidth, mx: 'auto' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SVG_W} ${HILL_H + AXIS_H}`}
        style={{ width: '100%', display: 'block', overflow: 'visible', touchAction: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={buildHillPath(SVG_W, BASELINE, AMPLITUDE)}
          fill="none"
          stroke="#525252"
          strokeWidth="1.5"
        />
        <line
          x1={peakX}
          y1={peakY - 4}
          x2={peakX}
          y2={BASELINE}
          stroke="#404040"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
        <line x1="0" y1={BASELINE} x2={SVG_W} y2={BASELINE} stroke="#404040" strokeWidth="1" />

        {/* An empty hill gets a ghost dot so the first scope has somewhere to
            come from. It is replaced by the real dots as soon as one exists. */}
        {scopes.length === 0 && onPlaceholderClick && (
          <g
            style={{ cursor: 'pointer' }}
            onClick={onPlaceholderClick}
          >
            <title>Add the first scope to this bet</title>
            <circle
              cx={(SVG_W * PLACEHOLDER_FRACTION).toFixed(1)}
              cy={hillY(PLACEHOLDER_FRACTION, BASELINE, AMPLITUDE).toFixed(1)}
              r={RADIUS}
              fill="#1a1a1a"
              stroke="#525252"
              strokeWidth="1.5"
              strokeDasharray="3,2.5"
            />
            <text
              x={(SVG_W * PLACEHOLDER_FRACTION + RADIUS + 7).toFixed(1)}
              y={(hillY(PLACEHOLDER_FRACTION, BASELINE, AMPLITUDE) + 3.5).toFixed(1)}
              fontSize="9"
              fill="#a3a3a3"
              fontWeight="600"
              style={{ userSelect: 'none' }}
            >
              Add the first scope
            </text>
          </g>
        )}

        {dots.map((dot) => {
          // Only the dot in question, unless someone asked to see the whole cycle
          // at once. Every trail at all times is noise, not information.
          if (!showTrails && dot.id !== selectedScopeId) return null;

          // The dot's own current position is the last point, so it is dropped:
          // the breadcrumbs are where it has been, not where it is.
          const past = (trails[dot.id] || []).slice(0, -1);
          if (!past.length) return null;

          const color = zoneColor(dot.fraction);
          return (
            <g key={`trail-${dot.id}`} pointerEvents="none">
              {past.map((point, index) => (
                <circle
                  key={`${dot.id}-${index}`}
                  cx={(point.position * SVG_W).toFixed(1)}
                  cy={hillY(point.position, BASELINE, AMPLITUDE).toFixed(1)}
                  r={3}
                  fill={color}
                  // Older crumbs fade, so the direction of travel reads at a glance.
                  fillOpacity={(0.15 + 0.35 * ((index + 1) / past.length)).toFixed(2)}
                />
              ))}
            </g>
          );
        })}

        {dots.map((dot) => {
          const color = zoneColor(dot.fraction);
          const isDone = dot.jiraStatusCategory === 'done';
          const isSelected = dot.id === selectedScopeId;
          const isDragging = drag && drag.scopeId === dot.id;
          // A dot somebody placed is the only one that carries a judgement about
          // the unknowns, so it is the solid one. Everything else is filled in
          // from Jira, and reads progressively fainter the less it is standing on:
          // a mapped status is the team's own translation of its workflow, while a
          // provisional dot was put there by the status category because nobody
          // mapped the status at all. Drawing all three identically would let the
          // chart borrow a confidence nobody gave it.
          // Finished work is exempt: "done" is the one thing Jira can report
          // without interpreting anything, so it is drawn as solidly as a
          // judgement rather than as a guess.
          const isJudged = dot.positionSource === 'override' || isDone;
          const provisional = Boolean(dot.isProvisional) && !isDone;
          return (
            <g
              key={`dot-${dot.id}`}
              className="hill-dot-group"
              style={{ cursor: readOnly ? 'pointer' : (isDragging ? 'grabbing' : 'grab') }}
              onPointerDown={(event) => handlePointerDown(event, dot)}
              onPointerMove={handlePointerMove}
              onPointerUp={(event) => handlePointerUp(event, dot)}
              onPointerCancel={handlePointerCancel}
              onClick={() => { if (readOnly && onSelectScope) onSelectScope(dot); }}
              onMouseEnter={() => setHoveredScope(dot)}
              onMouseLeave={() => setHoveredScope(null)}
            >
              <title>
                {`${dot.jiraKey} · ${dot.jiraSummary || ''} · ${dot.jiraStatus || 'Unknown'}`
                  + (dot.positionSource === 'override'
                    ? ' · placed by hand'
                    : (provisional ? ' · guessed from the status category' : ' · from the Jira status'))
                  + (dot.lastJudgmentNote ? ` · ${dot.lastJudgmentNote}` : '')
                  + (dot.isStuck ? ` · hasn't moved in ${dot.stillForDays} days` : '')}
              </title>
              {/* An amber halo marks a dot that has sat still long enough to be
                  worth asking about. */}
              {dot.isStuck && (
                <circle
                  cx={dot.x.toFixed(1)}
                  cy={dot.y.toFixed(1)}
                  r={RADIUS + 6}
                  fill="none"
                  stroke="#f59e0b"
                  strokeOpacity="0.75"
                  strokeWidth="2"
                />
              )}
              <circle
                cx={dot.x.toFixed(1)}
                cy={dot.y.toFixed(1)}
                r={RADIUS}
                fill={provisional ? '#1a1a1a' : color}
                fillOpacity={isJudged || provisional ? 1 : 0.5}
                stroke={isSelected ? '#ffffff' : (provisional ? color : '#1a1a1a')}
                strokeOpacity={provisional && !isSelected ? 0.8 : 1}
                strokeWidth={isSelected ? 2.5 : 2}
                strokeDasharray={provisional ? '3,2' : undefined}
              />
              {isDone && (
                <text
                  x={dot.x.toFixed(1)}
                  y={(dot.y + 4).toFixed(1)}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#0a0a0a"
                  fontWeight="800"
                  pointerEvents="none"
                >
                  ✓
                </text>
              )}
            </g>
          );
        })}

        {/* Centered display area for hovered scope */}
        {hoveredScope && (
          <text
            x={peakX}
            y={BASELINE - 30}
            textAnchor="middle"
            fontSize="16"
            fill="#ffffff"
            fontWeight="700"
            style={{ userSelect: 'none' }}
          >
            <tspan fontWeight="800" fill="#e5e5e5">{hoveredScope.jiraKey}</tspan>
            <tspan dx="6">{hoveredScope.jiraSummary || ''}</tspan>
          </text>
        )}

        <text
          x={(SVG_W * 0.02).toFixed(1)}
          y={(BASELINE + 20).toFixed(1)}
          textAnchor="start"
          fontSize="9.5"
          fill="#a3a3a3"
          fontWeight="700"
          letterSpacing="1.2"
        >
          FIGURING THINGS OUT
        </text>
        <text
          x={(SVG_W * 0.98).toFixed(1)}
          y={(BASELINE + 20).toFixed(1)}
          textAnchor="end"
          fontSize="9.5"
          fill="#a3a3a3"
          fontWeight="700"
          letterSpacing="1.2"
        >
          MAKING IT HAPPEN
        </text>
      </svg>
    </Box>
  );
};

export default memo(HillChart);
